import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneTilt, FilePdf, SpinnerGap, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";

export function ProposalDialog({ lead, open, onOpenChange, onSent }) {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);

  const preview = async (n) => {
    const { data } = await api.get(`/leads/${lead.id}/proposal`, { params: { note: n } });
    setHtml(data.html);
    return data;
  };
  useEffect(() => {
    if (!open) return;
    setNote("");
    preview("").then((d) => { setTo(d.to); setSubject(d.subject); }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => preview(note).catch(() => {}), 500);
    return () => clearTimeout(t);
  }, [note]); // eslint-disable-line react-hooks/exhaustive-deps

  const brochure = async () => {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get("/brochure.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      if (win) win.location.href = url; else window.location.assign(url);
    } catch (e) { win?.close(); toast.error("Could not open the brochure"); }
  };
  const send = async () => {
    setBusy(true);
    try { await api.post(`/leads/${lead.id}/proposal`, { to, subject, note }, { timeout: 60000 }); toast.success(`Proposal sent to ${to}`); onOpenChange(false); onSent?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not send the proposal"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-slate-200 max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="proposal-dialog">
        <DialogHeader><DialogTitle className="font-heading">Send Company Proposal</DialogTitle><DialogDescription>Review the email, add a personal note, then send. The company brochure with plans & pricing is attached automatically.</DialogDescription></DialogHeader>
        <div className="grid lg:grid-cols-[320px_1fr] gap-5">
          <div className="space-y-3">
            <label className="block"><span className="text-xs font-semibold text-slate-500">To</span><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="owner@carrier.com" className="bg-white border-slate-300 mt-1" data-testid="proposal-to" /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-500">Subject</span><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="bg-white border-slate-300 mt-1" data-testid="proposal-subject" /></label>
            <label className="block"><span className="text-xs font-semibold text-slate-500">Personal note (optional)</span><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={6} placeholder="Great speaking with you today about your two reefers…" className="bg-white border-slate-300 mt-1" data-testid="proposal-note" /></label>
            <button onClick={brochure} className="w-full flex items-center gap-3 rounded-xl border border-slate-200 p-3 text-left hover:border-blue-400 transition-colors" data-testid="proposal-brochure">
              <span className="w-10 h-10 rounded-lg bg-red-50 text-red-600 grid place-items-center"><FilePdf size={22} weight="duotone" /></span>
              <span className="min-w-0"><span className="block text-sm font-semibold text-slate-900 truncate">Asterism-Services-Brochure.pdf</span><span className="text-xs text-slate-500 flex items-center gap-1"><Paperclip size={12} />Attached · click to preview</span></span>
            </button>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-slate-100">
            {html ? <iframe title="Proposal preview" srcDoc={html} className="w-full h-[60vh] bg-white" data-testid="proposal-preview" /> : <div className="h-[60vh] grid place-items-center"><SpinnerGap size={24} className="animate-spin text-slate-400" /></div>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
          <Button onClick={send} disabled={busy || !to || !subject} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="proposal-send">{busy ? <SpinnerGap size={16} className="mr-1.5 animate-spin" /> : <PaperPlaneTilt size={16} className="mr-1.5" />}{busy ? "Sending…" : "Send Proposal"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
